import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NewCreatorModal from '../Modals/NewCreatorModal.js';
import EditUserModal from '../Modals/EditUserModal.js';
import {
    Container,
    Col,
    Toast,
    ToastBody,
    ToastHeader
} from "reactstrap";
import { s } from 'framer-motion/client';

function AdminCreator() {
    const [users, setUsers] = useState([]);           // Liste de tous les créateurs
    const [folders, setFolders] = useState([]);       // Emails des créateurs qui ont des contenus
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userlvl, setUserlvl] = useState(null);     // rôle de l'utilisateur connecté

    const [editingUser, setEditingUser] = useState(null);
    const [isEditOpen, setIsEditOpen] = useState(false);

    const navigate = useNavigate();

    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    const showSuccess = (message) => {
        setShowErrorToast(false);
        setToastMessage(message);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
    };

    const showError = (message) => {
        setShowSuccessToast(false);
        setToastMessage(message);
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 3000);
    };

    // Fetch des utilisateurs et filtrage créateurs
    const fetchCreators = async () => {
        try {
            const res = await fetch('/api/user/list', { credentials: 'include' });
            if (!res.ok) throw new Error("Erreur fetch users");
            const data = await res.json();
            const creators = data.filter(u => Number(u.user_ROLE_ID) === 4); // rôle créateur
            setUsers(creators);
        } catch (e) {
            console.error(e);
            setError(e);
        }
    };

    // Fetch des dossiers existants (emails qui ont du contenu)
    const fetchFolders = async () => {
        try {
            const res = await fetch('/api/data/admin/list?adminView=true', { credentials: 'include' });
            if (!res.ok) throw new Error("Erreur fetch folders");
            const data = await res.json(); // tableau d'emails
            setFolders(data);
        } catch (e) {
            console.error(e);
        }
    };

    // Récupérer rôle de l'utilisateur connecté
    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("authUser"));
        if (userData) setUserlvl(userData.user_ROLE_ID);
    }, []);

    // Fetch utilisateurs et dossiers dès que userlvl est défini
    useEffect(() => {
        if (userlvl !== null) {
            setLoading(true);
            Promise.all([fetchCreators(), fetchFolders()])
                .finally(() => setLoading(false));
        }
    }, [userlvl]);

    const handleImpersonate = async (email) => {
        try {
            const res = await fetch('/api/admin/impersonate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email })
            });
            if (!res.ok) throw new Error("Impossible de démarrer l'impersonation");
            window.dispatchEvent(new Event('impersonation-changed'));
            navigate('/homemono');
        } catch (e) {
            console.error(e);
            alert(e.message || 'Erreur impersonation');
        }
    };

    const handleResetPassword = async (email) => {
        try {
            const newPassword = prompt("Entrez le nouveau mot de passe pour l'utilisateur :");
            if (!newPassword) return;

            const res = await fetch('/api/user/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ user_EMAIL: email, newPassword })
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || 'Erreur lors de la réinitialisation');
            }

            showSuccess("Mot de passe réinitialisé avec succès !");
        } catch (err) {
            console.error(err);
            showError(err.message || "Erreur lors de la réinitialisation du mot de passe");
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Voulez-vous vraiment supprimer ce créateur ?")) return;

        try {
            await fetch(`/api/user/delete/${userId}`, { method: "DELETE", credentials: "include" });
            fetchCreators();
            fetchFolders();
            showSuccess("Créateur supprimé avec succès !");
        } catch (e) {
            console.error(e);
            showError("Erreur lors de la suppression du créateur");
        }
    };

    const handleUserCreated = () => {
        fetchCreators();
        fetchFolders();
        showSuccess("Opération réussie !");
    };

    if (loading) return <div className="p-4 text-center">Chargement des données...</div>;
    if (error) return <div className="p-4 text-center text-danger">Erreur : {error.message}</div>;

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Col lg={12}>
                        <div className="card">
                            <div className="card-body">
                                <h1 className="text-primary text-center">Créateurs</h1>
                                <h2 className="text-tertiary mb-4 text-center">
                                    Gestion des comptes
                                </h2>
                                <p className="text-secondary mb-2 text-center">
                                    ℹ️ Mode Administrateur
                                </p>

                                {users.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-centered table-nowrap mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Nom</th>
                                                    <th>Prénom</th>
                                                    <th>Email</th>
                                                    <th>Statut</th>
                                                    <th style={{ width: "50px" }}>Actions</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {users.map(user => {
                                                    const hasContent = folders.includes(user.user_EMAIL);
                                                    return (
                                                        <tr key={user.user_ID}>
                                                            <td>{user.user_SURNAME}</td>
                                                            <td>{user.user_NAME}</td>
                                                            <td>
                                                                <button
                                                                    className="text-primary bg-transparent border-0"
                                                                    onClick={() => handleImpersonate(user.user_EMAIL)}
                                                                    title={hasContent ? "Incarner ce créateur" : "Créer du contenu pour ce créateur"}
                                                                >
                                                                    {user.user_EMAIL}
                                                                </button>
                                                            </td>
                                                            {/* Nouvelle colonne Statut */}
                                                            <td>
                                                                <span className={`badge ${hasContent ? 'bg-success' : 'bg-secondary'}`}>
                                                                    {hasContent ? 'Contenu disponible' : 'Aucun contenu'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    title="Incarner ce créateur"
                                                                    onClick={() => handleImpersonate(user.user_EMAIL)}
                                                                    className="btn btn-sm btn-outline-primary fs-4 py-0 px-1 me-2"
                                                                >
                                                                    <i className="mdi mdi-account-arrow-left-outline"></i>
                                                                </button>

                                                                <button
                                                                    title="Réinitialiser le mot de passe"
                                                                    onClick={() => handleResetPassword(user.user_EMAIL)}
                                                                    className="btn btn-sm btn-outline-warning fs-4 py-0 px-1 me-2"
                                                                >
                                                                    <i className="mdi mdi-key"></i>
                                                                </button>

                                                                <button
                                                                    title="Modifier l'utilisateur"
                                                                    className="btn btn-sm btn-outline-secondary fs-4 py-0 px-1 me-2"
                                                                    onClick={() => {
                                                                        setEditingUser(user);
                                                                        setIsEditOpen(true);
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-account-edit-outline"></i>
                                                                </button>

                                                                <button
                                                                    title="Supprimer ce Créateur"
                                                                    className="btn btn-sm btn-outline-danger fs-4 py-0 px-1 me-2"
                                                                    onClick={() => handleDeleteUser(user.user_ID)}
                                                                >
                                                                    <i className="mdi mdi-account-off-outline"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>

                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-center">Aucun compte créateur trouvé.</p>
                                )}

                                <div className="mb-3 mt-4 d-flex justify-content-center">
                                    <NewCreatorModal onUserCreated={handleUserCreated} />
                                </div>

                            </div>
                        </div>
                    </Col>
                </Container>
            </div>

            <EditUserModal
                isOpen={isEditOpen}
                toggle={() => setIsEditOpen(false)}
                user={editingUser}
                onSaved={() => {
                    handleUserCreated();
                    showSuccess("Utilisateur modifié avec succès");
                }}
            />

            {/* Toast de succès */}
            <div
                style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 9999,
                }}
            >
                <Toast isOpen={showSuccessToast}>
                    <ToastHeader
                        icon="success"
                        toggle={() => setShowSuccessToast(false)}
                        style={{ backgroundColor: '#d4edda', color: '#155724' }}
                    >
                        Succès
                    </ToastHeader>
                    <ToastBody style={{ backgroundColor: '#d4edda', color: '#155724' }}>
                        {toastMessage}
                    </ToastBody>
                </Toast>
            </div>

            {/* Toast d'erreur */}
            <div
                style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 9999,
                }}
            >
                <Toast isOpen={showErrorToast}>
                    <ToastHeader
                        icon="danger"
                        toggle={() => setShowErrorToast(false)}
                        style={{ backgroundColor: '#f8d7da', color: '#721c24' }}
                    >
                        Erreur
                    </ToastHeader>
                    <ToastBody style={{ backgroundColor: '#f8d7da', color: '#721c24' }}>
                        {toastMessage}
                    </ToastBody>
                </Toast>
            </div>
        </React.Fragment>
    );
}

export default AdminCreator;

//Creators : Florian Cohen-Joly @3DDGAMES -- Under license